/**
 * ambientMusic.ts — v4
 * ─────────────────────────────────────────────────────────
 * Ciepły, spokojny podkład harmoniczny.
 *
 * Filozofia:
 *   - TYLKO pad harmoniczny — żadnych niespodzianek, dzwonów,
 *     melodii, pulsów. Samo brzmienie.
 *   - Co 15–25 sek głośność opada płynnie na 5–10 sek
 *     ("oddech") — użytkownik może chwilę odpocząć.
 *   - Tonacja: C-dur / A-moll pentatonika — ciepła, neutralna.
 *   - Stan autostartu przechowywany w module (nie w React) —
 *     nie resetuje się przy zmianie podstrony.
 *
 * Styl: Brian Eno "Music for Airports" — warstwa 1 i tylko ona.
 * ─────────────────────────────────────────────────────────
 */

/* ─── Akordy padu — szerokie, otwarte kwinty ────────── */
const PAD_CHORDS: number[][] = [
  [65.41,  130.81, 196.00, 261.63], // C2 C3 G3 C4
  [73.42,  146.83, 220.00, 293.66], // D2 D3 A3 D4
  [55.00,  110.00, 164.81, 220.00], // A1 A2 E3 A3
  [65.41,  130.81, 164.81, 261.63], // C2 C3 E3 C4 — ciepłe
  [73.42,  110.00, 196.00, 261.63], // D2 A2 G3 C4 — przestrzeń
];

function rnd(min: number, max: number): number {
  return min + Math.random() * (max - min);
}
function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/* ─── Stan autostartu — poza Reactem, trwa przez sesję ─ */
export const musicSessionState = {
  /** true = muzyka była już raz uruchomiona w tej sesji */
  hasAutoStarted: false,
  /** true = użytkownik ręcznie wyłączył muzykę */
  userMuted: false,
};

/* ─── Silnik ─────────────────────────────────────────── */
export class AmbientEngine {
  private ctx:        AudioContext | null = null;
  private masterGain: GainNode     | null = null;
  private reverbNode: ConvolverNode| null = null;
  private dryBus:     GainNode     | null = null;
  private wetBus:     GainNode     | null = null;
  private timers:     ReturnType<typeof setTimeout>[] = [];
  private running = false;

  get isRunning() { return this.running; }

  /* ── start ───────────────────────────────────────────── */
  async start() {
    if (this.running) return;

    this.ctx = new AudioContext();
    if (this.ctx.state === "suspended") await this.ctx.resume();

    /* Master gain — fade in 5 sek */
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.setValueAtTime(0, this.ctx.currentTime);
    this.masterGain.gain.linearRampToValueAtTime(0.46, this.ctx.currentTime + 5);

    /* Reverb (convolution z syntetycznym IR) */
    this.reverbNode = this._makeReverb(this.ctx, 7.0);
    this.dryBus     = this.ctx.createGain();
    this.wetBus     = this.ctx.createGain();
    this.dryBus.gain.value = 0.50;
    this.wetBus.gain.value = 0.50;

    this.masterGain.connect(this.dryBus);
    this.masterGain.connect(this.reverbNode);
    this.reverbNode.connect(this.wetBus);
    this.dryBus.connect(this.ctx.destination);
    this.wetBus.connect(this.ctx.destination);

    this.running = true;
    this._startPadLayer();        // jedyna warstwa dźwięku
    this._scheduleBreathCycle();  // cykl oddechu głośności
  }

  /* ── stop ────────────────────────────────────────────── */
  stop() {
    if (!this.running || !this.ctx || !this.masterGain) return;
    this.running = false;

    const now = this.ctx.currentTime;
    this.masterGain.gain.cancelScheduledValues(now);
    this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, now);
    this.masterGain.gain.linearRampToValueAtTime(0, now + 3.0);

    setTimeout(() => {
      this.timers.forEach(clearTimeout);
      this.timers = [];
      this.ctx?.close();
      this.ctx        = null;
      this.masterGain = null;
      this.reverbNode = null;
      this.dryBus     = null;
      this.wetBus     = null;
    }, 3500);
  }

  toggle() {
    if (this.running) {
      musicSessionState.userMuted = true;
      this.stop();
    } else {
      musicSessionState.userMuted = false;
      this.start();
    }
  }

  /* ── Syntetyczny Reverb ──────────────────────────────── */
  private _makeReverb(ctx: AudioContext, dur: number): ConvolverNode {
    const sr  = ctx.sampleRate;
    const len = sr * dur;
    const buf = ctx.createBuffer(2, len, sr);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      for (let i = 0; i < len; i++) {
        const decay = Math.pow(1 - i / len, 3.0);
        d[i] = (Math.random() * 2 - 1) * decay * 0.45;
      }
    }
    const c = ctx.createConvolver();
    c.buffer = buf;
    return c;
  }

  /* ── Warstwa padu ────────────────────────────────────── */
  // Jeden akord = kilka oscylatorów z mikrodetuningiem.
  // Co 18–26 sek akord zmienia się płynnie (crossfade).
  private _startPadLayer() {
    if (!this.ctx || !this.masterGain) return;

    const chord = pick(PAD_CHORDS);

    chord.forEach((freq) => {
      if (!this.ctx || !this.masterGain) return;

      // Każda nuta: sine (bass) lub triangle (mid) + lekki detune
      [-5, 0, +5].forEach((detune, di) => {
        if (!this.ctx || !this.masterGain) return;

        const osc  = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        const filt = this.ctx.createBiquadFilter();

        osc.type = freq < 150 ? "sine" : "triangle";
        osc.frequency.value = freq;
        osc.detune.value    = detune;

        filt.type            = "lowpass";
        filt.frequency.value = freq < 150 ? 280 : 1100;
        filt.Q.value         = 0.35;

        // Głośność bazowa — środkowy oscylator głośniejszy
        const vol = (freq < 150 ? 0.020 : 0.013) * (di === 1 ? 1.0 : 0.65);
        gain.gain.setValueAtTime(0,   this.ctx.currentTime);
        gain.gain.linearRampToValueAtTime(vol, this.ctx.currentTime + 6);

        osc.connect(filt);
        filt.connect(gain);
        gain.connect(this.masterGain);
        osc.start();
      });
    });

    // Crossfade do nowego akordu co 18–26 sek
    const changeIn = rnd(18000, 26000);
    const t = setTimeout(() => {
      if (this.running) this._startPadLayer();
    }, changeIn);
    this.timers.push(t);
  }

  /* ── Cykl oddechu głośności ──────────────────────────── */
  // Co 15–25 sek master gain płynnie opada do ~25% na 5–10 sek,
  // potem wraca. Użytkownik "oddycha" razem z muzyką.
  private _scheduleBreathCycle() {
    if (!this.running) return;

    // Czas do następnego oddechu: 15–25 sek normalnej głośności
    const waitMs    = rnd(15000, 25000);
    // Czas trwania ciszy: 5–10 sek
    const breathMs  = rnd(5000, 10000);
    // Głośność podczas oddechu: 20–30% normalnej
    const quietVol  = rnd(0.10, 0.14);
    const fullVol   = 0.46;
    // Czas przejść
    const fadeDown  = 3.5;  // sek opadania
    const fadeUp    = 4.5;  // sek powrotu

    const t = setTimeout(() => {
      if (!this.running || !this.ctx || !this.masterGain) return;

      const now = this.ctx.currentTime;

      // Opuść głośność
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, now);
      this.masterGain.gain.linearRampToValueAtTime(quietVol, now + fadeDown);

      // Po czasie oddechu — wróć do pełnej głośności
      const t2 = setTimeout(() => {
        if (!this.running || !this.ctx || !this.masterGain) return;
        const n2 = this.ctx.currentTime;
        this.masterGain.gain.cancelScheduledValues(n2);
        this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, n2);
        this.masterGain.gain.linearRampToValueAtTime(fullVol, n2 + fadeUp);

        // Zaplanuj kolejny oddech
        this._scheduleBreathCycle();
      }, breathMs);

      this.timers.push(t2);
    }, waitMs);

    this.timers.push(t);
  }
}

/* Singleton */
export const ambientEngine = new AmbientEngine();
